import React from "react";
import {
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CustomDatePicker } from "../../../HelperFunctions/EmployeeFormHelpers";
import { payrollSetupStyles } from "../../../styles/payrollSetup.styles";

/**
 * PayrollStatusSection Component
 *
 * Handles:
 * 1. "Clone for all operations" option across corporate entities.
 * 2. Active status toggle (edit mode).
 * 3. "Update COA changes to Previous Transactions" toggle and retroactive effective date selector.
 */
const PayrollStatusSection = ({
  values,
  setFieldValue,
  isEditMode = false,
  corporations = [],
  selectedCloneCorps = [],
  setSelectedCloneCorps,
  setClonePopupOpen,
  coaDateKey = 0,
  setCoaDateKey,
  setConfirmCoaDateChange,
}) => {
  const isWages = values.itemType === "wages";
  const hasWageType = Boolean(values.wageType);
  const showPayType = isWages && hasWageType;

  // Selected clone properties count (excluding current corporation)
  const currentCorpValue = String(values.corporation?.corpValue || "");
  const otherSelectedCorps = selectedCloneCorps.filter(
    (id) => String(id) !== currentCorpValue
  );
  const cloneCount = otherSelectedCorps.length;

  return (
    <Stack direction="row" gap={5} mt={-1}>
      {/* Clone Across Operations */}
      <Box minWidth={200} whiteSpace="nowrap">
        <Stack direction="column">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                name="clone"
                checked={Boolean(values.clone)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFieldValue("clone", checked);
                  if (checked) {
                    const allCorps = corporations
                      .filter((c) => c.corpValue !== "")
                      .map((c) => c.corpValue);
                    setSelectedCloneCorps(allCorps);
                  } else {
                    setSelectedCloneCorps([]);
                  }
                }}
              />
            }
            label={<span title="Clone for all operations">Clone for all operations</span>}
            sx={payrollSetupStyles.cloneCheckboxLabel}
          />

          {values.clone && (
            <Typography
              variant="caption"
              sx={payrollSetupStyles.cloneCountText}
              onClick={() => setClonePopupOpen(true)}
              title={`for ${cloneCount} ${cloneCount === 1 ? "property" : "properties"}`}
            >
              <span title={`for ${cloneCount} ${cloneCount === 1 ? "property" : "properties"}`}>
                {`for ${cloneCount} ${cloneCount === 1 ? "property" : "properties"}`}
              </span>
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Edit Mode Only: Active Status & COA Retroactive Update */}
      {isEditMode && (
        <>
          <Box minWidth={200}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  name="active"
                  checked={Boolean(values.active)}
                  onChange={(e) => setFieldValue("active", e.target.checked)}
                />
              }
              label={<span title="Active">Active</span>}
              sx={payrollSetupStyles.activeCheckboxLabel}
            />
          </Box>

          {(!isWages || showPayType) && (
            <Box minWidth={200} whiteSpace="nowrap">
              <Stack direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      name="updateCOA"
                      checked={Boolean(values.updateCOA)}
                      onChange={(e) =>
                        setFieldValue("updateCOA", e.target.checked)
                      }
                    />
                  }
                  label={
                    <span title="Update COA changes to Previous Transactions">
                      Update COA changes to Previous Transactions
                    </span>
                  }
                  sx={payrollSetupStyles.updateCoaLabel}
                />

                {values.updateCOA && (
                  <CustomDatePicker
                    key={coaDateKey}
                    heading=""
                    name="updateCOADate"
                    value={values.updateCOADate || null}
                    setFieldValue={(name, newDate) => {
                      const prevDate = values.updateCOADate || null;
                      const isSame =
                        (!newDate && !prevDate) ||
                        (newDate &&
                          prevDate &&
                          (newDate === prevDate ||
                            (dayjs(newDate).isValid() &&
                              dayjs(prevDate).isValid() &&
                              dayjs(newDate).isSame(dayjs(prevDate), "day"))));

                      if (isSame) return;

                      // Request user confirmation before applying retroactive COA change
                      setConfirmCoaDateChange({
                        open: true,
                        onConfirm: () => {
                          setFieldValue(name, newDate || null);
                          setConfirmCoaDateChange({
                            open: false,
                            onConfirm: () => {},
                            onCancel: () => {},
                          });
                        },
                        onCancel: () => {
                          setFieldValue(name, prevDate);
                          setCoaDateKey((prev) => prev + 1);
                          setConfirmCoaDateChange({
                            open: false,
                            onConfirm: () => {},
                            onCancel: () => {},
                          });
                        },
                      });
                    }}
                    labelWidth={0}
                    width={150}
                    spacing={0}
                    disableFuture={true}
                  />
                )}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default React.memo(PayrollStatusSection);
